/**
 * The loops behind the proposals a person is waiting on, read directly.
 *
 * The proposal store holds only the cycles the dashboard refresh asked for, so
 * a proposal from another cycle would leave its waiting line unable to name
 * who has it. This reads the few proposals on the waiting list and their loops'
 * holders in two small queries, under the person's own row-level security.
 */
import { useEffect, useMemo, useState } from 'react';
import { getTypedSupabaseClient } from '../../lib/typedSupabaseClient';

export interface WaitingProposalLoops {
  proposals: { id: string; loopId: string | null }[];
  loops: { id: string; ballHolderUserId: string | null; ballClaimedAt: string | null }[];
}

const EMPTY: WaitingProposalLoops = { proposals: [], loops: [] };

export function useWaitingProposalLoops(proposalIds: string[]): WaitingProposalLoops {
  const key = useMemo(() => [...new Set(proposalIds)].sort().join(','), [proposalIds]);
  const [state, setState] = useState<WaitingProposalLoops>(EMPTY);

  useEffect(() => {
    const ids = key ? key.split(',') : [];
    if (ids.length === 0) {
      setState(EMPTY);
      return;
    }
    let cancelled = false;
    void (async () => {
      const supabase = getTypedSupabaseClient();
      const { data: props, error } = await supabase.from('catchball_proposals').select('id, loop_id').in('id', ids);
      if (cancelled || error || !props) return;
      const loopIds = [...new Set(props.map((p) => p.loop_id).filter((x): x is string => Boolean(x)))];
      const { data: loops } = loopIds.length
        ? await supabase.from('catchball_loops').select('id, ball_holder_user_id, ball_claimed_at').in('id', loopIds)
        : { data: [] as { id: string; ball_holder_user_id: string | null; ball_claimed_at: string | null }[] };
      if (cancelled) return;
      setState({
        proposals: props.map((p) => ({ id: p.id, loopId: p.loop_id ?? null })),
        loops: (loops ?? []).map((l) => ({ id: l.id, ballHolderUserId: l.ball_holder_user_id ?? null, ballClaimedAt: l.ball_claimed_at ?? null })),
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [key]);

  return state;
}
